import { Logo } from "@/components/Logo";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Shield, Loader2, Dumbbell, MapPin, MessageSquare, Apple, Video, ListChecks, X } from "lucide-react";

const features = [
  { icon: MessageSquare, text: "AI fitness coach" },
  { icon: Apple, text: "AI nutrition coach + calculator" },
  { icon: ListChecks, text: "Auto-built training programs" },
  { icon: MapPin, text: "50-workout guided running library" },
  { icon: Dumbbell, text: "Full workout library + timers" },
  { icon: Video, text: "YouTube exercise video library" },
];

export default function Subscribe() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [plan, setPlan] = useState("annual");
  const [loading, setLoading] = useState(null);
  const [plans, setPlans] = useState({
    monthly: { amount: 9.99, interval: "month" },
    annual: { amount: 99.99, interval: "year" },
    trial_days: 7,
  });

  useEffect(() => {
    api.get("/billing/plans").then(({ data }) => setPlans(data)).catch(() => {});
  }, []);

  const subscribe = async (planKey) => {
    setLoading(planKey);
    try {
      const origin = window.location.origin;
      const { data } = await api.post("/billing/checkout-session", { plan: planKey, origin_url: origin });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start checkout");
      setLoading(null);
    }
  };

  const trialing = user?.access?.status === "trialing";
  const trialDays = plans.trial_days || 7;
  const monthlyEquivFromAnnual = plans.annual?.amount ? (plans.annual.amount / 12).toFixed(2) : null;

  const closePaywall = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate("/dashboard");
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="rd-hero-sheen px-6 pt-10 pb-6">
        <div className="max-w-[480px] mx-auto relative">
          <button
            data-testid="close-paywall-button"
            onClick={closePaywall}
            aria-label="Close"
            className="absolute -top-2 right-0 h-9 w-9 rounded-full bg-card border border-border flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-[#ccff0c]/40 transition-colors z-10"
          >
            <X className="h-5 w-5" />
          </button>
          <Logo className="h-16 w-16 rounded-2xl mb-4" />
          <Badge data-testid="trial-7-day-badge" className="bg-secondary text-[#ccff0c] border border-[#ccff0c]/30 mb-3">
            <Shield className="h-3.5 w-3.5 mr-1" /> {trialDays}-day free trial
          </Badge>
          <h1 className="font-display text-4xl font-bold leading-tight">Unlock everything</h1>
          <p className="text-muted-foreground mt-2">
            {trialing
              ? `Your trial is active. Lock in your plan now — you won't be charged until your ${trialDays}-day trial ends.`
              : "Choose a plan to unlock AI coaching, nutrition, programs, and the running library."}
          </p>
        </div>
      </div>

      <div className="max-w-[480px] mx-auto px-6 pb-32">
        <div className="space-y-3 mb-6">
          <button
            data-testid="plan-annual"
            onClick={() => setPlan("annual")}
            className={`w-full text-left rounded-2xl border p-4 transition-colors ${plan === "annual" ? "border-[#ccff0c] bg-card rd-glow" : "border-border bg-card"}`}
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-display text-xl font-bold">Annual</span>
                  <Badge className="bg-[#ccff0c] text-black">Best value</Badge>
                </div>
                <p className="text-muted-foreground text-sm mt-1">
                  ${plans.annual?.amount}/year
                  {monthlyEquivFromAnnual ? <span className="text-muted-foreground/70"> · just ${monthlyEquivFromAnnual}/mo</span> : null}
                </p>
              </div>
              <div className={`h-5 w-5 rounded-full border-2 ${plan === "annual" ? "border-[#ccff0c] bg-[#ccff0c]" : "border-border"}`} />
            </div>
          </button>
          <button
            data-testid="plan-monthly"
            onClick={() => setPlan("monthly")}
            className={`w-full text-left rounded-2xl border p-4 transition-colors ${plan === "monthly" ? "border-[#ccff0c] bg-card rd-glow" : "border-border bg-card"}`}
          >
            <div className="flex items-center justify-between">
              <div>
                <span className="font-display text-xl font-bold">Monthly</span>
                <p className="text-muted-foreground text-sm mt-1">${plans.monthly?.amount}/month</p>
              </div>
              <div className={`h-5 w-5 rounded-full border-2 ${plan === "monthly" ? "border-[#ccff0c] bg-[#ccff0c]" : "border-border"}`} />
            </div>
          </button>
        </div>

        <div className="space-y-2.5 mb-8">
          {features.map((f, i) => {
            const Icon = f.icon;
            return (
              <div key={i} className="flex items-center gap-3 text-sm">
                <div className="h-8 w-8 rounded-lg bg-card border border-border flex items-center justify-center">
                  <Icon className="h-4 w-4 text-[#ccff0c]" />
                </div>
                <span className="text-muted-foreground">{f.text}</span>
              </div>
            );
          })}
        </div>

        <Button
          data-testid={plan === "annual" ? "subscribe-annual-button" : "subscribe-monthly-button"}
          onClick={() => subscribe(plan)}
          disabled={loading !== null}
          className="w-full h-12 bg-[#ccff0c] text-black hover:bg-[#d7ff3a] rd-glow font-semibold text-base"
        >
          {loading === plan ? <Loader2 className="h-5 w-5 animate-spin" /> : `Start ${trialDays}-day free trial`}
        </Button>
        <p className="text-center text-xs text-muted-foreground mt-3">
          {plan === "annual"
            ? `Then $${plans.annual?.amount}/year after your ${trialDays}-day trial. Cancel anytime.`
            : `Then $${plans.monthly?.amount}/month after your ${trialDays}-day trial. Cancel anytime.`}
        </p>

        {trialing && (
          <button data-testid="continue-trial" onClick={() => navigate("/dashboard")} className="w-full mt-4 text-center text-sm text-muted-foreground hover:text-foreground">
            Continue with free trial
          </button>
        )}
      </div>
    </div>
  );
}
